// Calendar page (/calendar): next central-bank decisions with countdowns, the
// week's stock-market status, and an agenda of events grouped by day.
// Data: /api/calendar (backend/src/calendar/). Every time is computed from the
// UTC `at` and shown in the visitor's own time zone. No forecasts, ever.
// Design: docs/calendar-mockups (direction A + countdown cards and market table from C).

import '../styles/main.css';
import '../styles/features.css';
import '../styles/calendar.css';
import { t, lang, applyStatic, initLanguagePicker, countryName } from '../i18n/index.js';
import { initNavTools } from '../ui/navTools.js';
import { getFollows } from '../features/follow.js';
import { EXCHANGES as SESSION_EXCHANGES } from '../markets/sessions.js';
import { esc, safeHref, flag, localDay, addDays, eventDay, titleOf, fetchEvents, icsUrl, EURO_AREA } from './data.js';

const $ = (id) => document.getElementById(id);
const loc = lang === 'en' ? 'en-GB' : lang;
const USER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

// ---------------------------------------------------------------- formatting
const fmtCache = {};
const fmt = (opts, tz = USER_TZ) => (fmtCache[tz + JSON.stringify(opts)] ||= new Intl.DateTimeFormat(loc, { timeZone: tz, ...opts }));
const timeIn = (ts, tz = USER_TZ) => fmt({ hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }, tz).format(ts);
const noon = (k) => new Date(`${k}T12:00:00Z`);
const dayLong = (k) => fmt({ weekday: 'long', day: 'numeric', month: 'long' }, 'UTC').format(noon(k));
const dayShort = (k) => fmt({ weekday: 'short', day: 'numeric', month: 'short' }, 'UTC').format(noon(k));
const wdShort = (k) => fmt({ weekday: 'short' }, 'UTC').format(noon(k));
const monthShort = (k) => fmt({ month: 'short' }, 'UTC').format(noon(k));
const dayNum = (k) => Number(k.slice(8, 10));
const isWeekend = (k) => [0, 6].includes(noon(k).getUTCDay());
const mondayOf = (k) => addDays(k, -((noon(k).getUTCDay() + 6) % 7));
function tzAbbr() {
    const p = fmt({ timeZoneName: 'short' }).formatToParts(Date.now()).find((x) => x.type === 'timeZoneName');
    return p ? p.value : USER_TZ;
}
const CITY = {
    'America/New_York': 'New York', 'Europe/Berlin': 'Frankfurt', 'Europe/London': 'London', 'Asia/Tokyo': 'Tokyo', 'America/Toronto': 'Ottawa',
    'Australia/Sydney': 'Sydney', 'Pacific/Auckland': 'Wellington', 'Europe/Stockholm': 'Stockholm', 'Europe/Oslo': 'Oslo', 'Asia/Kolkata': 'Mumbai',
    'Asia/Shanghai': 'Shanghai', 'Asia/Hong_Kong': 'Hong Kong', 'Europe/Zurich': 'Zurich', 'Europe/Luxembourg': 'Luxembourg', 'Europe/Paris': 'Paris',
};
/** Official local time when it differs from the visitor's: "08:30 New York". */
function originTime(e) {
    if (!e.at || !e.tz || e.tz === USER_TZ) return '';
    const ts = Date.parse(e.at);
    const local = timeIn(ts, e.tz);
    return local === timeIn(ts) ? '' : `${local} ${CITY[e.tz] || e.tz.split('/').pop().replace(/_/g, ' ')}`;
}
let enNames = null;
try {
    enNames = new Intl.DisplayNames(['en'], { type: 'region' });
} catch {
    enNames = null;
}
const nameOf = (iso) => (iso === 'EU' ? t('cal.euroArea') : countryName(iso, enNames?.of(iso) || iso));
const newsHref = (iso) => (iso === 'EU' ? '/app' : `/app?country=${encodeURIComponent(iso)}`);

const ICONS = {
    central_bank: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 9.5 12 4l9 5.5M5 10v8M9.5 10v8M14.5 10v8M19 10v8M3 20h18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    data_release: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20V4M4 20h16M8 16v-4M12 16V8M16 16v-6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
    market_closed: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M6.5 17.5 17.5 6.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
    early_close: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M12 7.5V12l3 2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
    public_holiday: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 21V4M6 5h11l-2 3.5 2 3.5H6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    cal: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="5.5" width="16" height="14.5" rx="2" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M4 10h16M8.5 3.5v4M15.5 3.5v4M12 12.5v5M9.5 15h5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>',
    ext: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 5h5v5M19 5l-8 8M17 14v4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    news: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.2" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M3.8 12h16.4M12 3.8c2.6 2.4 2.6 14 0 16.4M12 3.8c-2.6 2.4-2.6 14 0 16.4" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>',
    rss: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5a14 14 0 0 1 14 14M5 11a8 8 0 0 1 8 8" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/><circle cx="6" cy="18" r="1.6" fill="currentColor"/></svg>',
    check: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5 10 17l9-10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
};

const GROUPS = [
    { id: 'central_bank', types: ['central_bank'] },
    { id: 'data_release', types: ['data_release'] },
    { id: 'markets', types: ['market_closed', 'early_close'] },
    { id: 'public_holiday', types: ['public_holiday'] },
];

// ---------------------------------------------------------------- header + static
applyStatic();
initLanguagePicker();
initNavTools();
document.title = `${t('cal.title')} — NewsNetWide`;
$('ca-promise').innerHTML = ['cal.promise1', 'cal.promise2', 'cal.promise3'].map((k) => `<li>${ICONS.check}${esc(t(k))}</li>`).join('');
$('subscribe').innerHTML = `${ICONS.rss}<span>${esc(t('cal.subscribe'))}</span>`;
$('foot-tz').textContent = t('cal.yourTz', { tz: `${USER_TZ}, ${tzAbbr()}` });
const tick = () => {
    $('ca-clock').querySelector('span').textContent = `${timeIn(Date.now())} ${t('cal.yourTimeShort')} · ${tzAbbr()}`;
};
tick();
setInterval(tick, 15000);

// ---------------------------------------------------------------- central banks (countdown cards)
let decisions = [];
function cdParts(ms) {
    const s = Math.max(0, Math.floor(ms / 1000));
    return { d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60) };
}
function renderBanks() {
    if (!decisions.length) return;
    const now = Date.now();
    const top = decisions.slice(0, 4);
    $('ca-banks').innerHTML = top
        .map((b) => {
            const ts = Date.parse(b.at);
            const p = cdParts(ts - now);
            const live = ts <= now;
            const origin = originTime(b);
            return `<article class="cc-bank">
                <div class="cc-bank-top"><span class="cc-bank-flag" aria-hidden="true">${flag(b.country)}</span>
                    <div><div class="cc-bank-code">${esc(b.short)} · ${esc(nameOf(b.country))}</div><div class="cc-bank-name">${esc(t(`bank.${b.bank}`))}</div></div>${impactBadge('high')}</div>
                <div class="cc-cd" role="timer">${live ? `<b>${esc(t('cal.now'))}</b>` : `<b>${p.d}</b><span>${esc(t('cal.unitD'))}</span><b>${p.h}</b><span>${esc(t('cal.unitH'))}</span><b>${p.m}</b><span>${esc(t('cal.unitM'))}</span>`}</div>
                <div class="cc-bank-when">${esc(dayShort(localDay(new Date(ts))))} · <b>${timeIn(ts)}</b> ${esc(t('cal.yourTimeShort'))}${origin ? ` · ${esc(origin)}` : ''}${b.approx ? `<span class="approx">${esc(t('cal.approxNote'))}</span>` : ''}</div>
                <div class="cal-actions">
                    <a class="cal-act" href="${safeHref(b.source)}" target="_blank" rel="noopener noreferrer" title="${esc(t('cal.source'))}">${ICONS.ext}<span class="vh">${esc(t('cal.source'))}</span></a>
                    <a class="cal-act grow" href="${icsUrl(b.id)}" download title="${esc(t('cal.addToCal'))}">${ICONS.cal}<span>${esc(t('cal.addShort'))}</span></a>
                    <a class="cal-act" href="${newsHref(b.country)}" title="${esc(t('cal.relatedNews', { country: nameOf(b.country) }))}">${ICONS.news}<span class="vh">${esc(t('cal.relatedNews', { country: nameOf(b.country) }))}</span></a>
                </div>
            </article>`;
        })
        .join('');
    $('ca-banks-more').innerHTML = decisions.length > 4
        ? `<li>${esc(t('cal.then'))}</li>` + decisions.slice(4).map((b) => `<li>${flag(b.country)} <b>${esc(b.short)}</b> ${esc(dayShort(localDay(new Date(b.at))))}, ${timeIn(Date.parse(b.at))}</li>`).join('')
        : '';
}
// ---------------------------------------------------------------- stock markets this week
const TSX = { id: 'tsx', short: 'TSX', city: 'Toronto', tz: 'America/Toronto', sessions: [['open', '09:30', '16:00']] };
const MKT_ORDER = ['nyse', 'tsx', 'lse', 'xetra', 'euronext', 'tse', 'hkex', 'sse', 'nse', 'asx'];
const MKT_COUNTRY = { nyse: 'US', tsx: 'CA', lse: 'GB', xetra: 'DE', euronext: 'FR', tse: 'JP', hkex: 'HK', sse: 'CN', nse: 'IN', asx: 'AU' };
const MKT_NAME = { nyse: 'NYSE · Nasdaq', tsx: 'Toronto (TSX)', lse: 'London SE', xetra: 'Xetra', euronext: 'Euronext', tse: 'Tokyo (JPX)', hkex: 'Hong Kong (HKEX)', sse: 'Shanghai (SSE)', nse: 'NSE · BSE', asx: 'ASX' };
const markets = MKT_ORDER.map((id) => {
    const ex = id === 'tsx' ? TSX : SESSION_EXCHANGES.find((e) => e.id === id);
    return ex && { id, tz: ex.tz, city: ex.city, open: ex.sessions.filter((s) => s[0] === 'open').map(([, a, b]) => [a, b]) };
}).filter(Boolean);
const hm = (s) => s.split(':').map(Number).reduce((h, m) => h * 60 + m);
function minutesIn(ts, tz) {
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(ts).map((x) => [x.type, x.value]));
    return Number(p.hour) * 60 + Number(p.minute);
}
function dayIn(ts, tz) {
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(ts).map((x) => [x.type, x.value]));
    return `${p.year}-${p.month}-${p.day}`;
}
let closures = [];
function marketDay(m, k) {
    if (isWeekend(k)) return { state: 'weekend' };
    const c = closures.find((e) => e.exchange === m.id && e.date === k);
    if (c?.type === 'market_closed') return { state: 'closed', reason: c.reason };
    if (c?.type === 'early_close') return { state: 'early', closeLocal: c.closeLocal, at: c.at };
    return { state: 'open' };
}
function marketNow(m) {
    const now = Date.now();
    const k = dayIn(now, m.tz);
    const day = marketDay(m, k);
    if (day.state !== 'open' && day.state !== 'early') return { ...day, now: 'closed' };
    const mins = minutesIn(now, m.tz);
    const sessions = m.open.map(([a, b]) => [hm(a), hm(b)]);
    if (day.state === 'early') sessions[sessions.length - 1][1] = hm(day.closeLocal);
    for (let i = 0; i < sessions.length; i++) {
        const [a, b] = sessions[i];
        if (mins < a) return { ...day, now: i === 0 ? 'pre' : 'break', at: now + (a - mins) * 60000 };
        if (mins < b) return { ...day, now: 'open', at: now + (b - mins) * 60000 };
    }
    return { ...day, now: 'after' };
}
function renderMarkets() {
    const today = localDay();
    const mon = mondayOf(today);
    const days = [0, 1, 2, 3, 4].map((i) => addDays(mon, i));
    const n = closures.filter((c) => c.type === 'market_closed' && c.date >= mon && c.date <= days[4]).length;
    $('ca-mkt-aside').textContent = t('cal.mktAside', { n, tz: tzAbbr() });
    const head = `<thead><tr><th>${esc(t('cal.exchange'))}</th><th>${esc(t('cal.rightNow'))}</th>${days.map((k) => `<th class="day${k === today ? ' today' : ''}">${esc(wdShort(k))} ${dayNum(k)}</th>`).join('')}</tr></thead>`;
    const rows = markets
        .map((m) => {
            const s = marketNow(m);
            const C = { open: 'var(--t-open)', pre: '#8e8e8e', break: 'var(--t-early)', after: '#8e8e8e', closed: 'var(--t-closed)' }[s.now];
            const label = s.now === 'open' ? t('cal.mkt.open') : s.now === 'pre' ? t('cal.mkt.pre') : s.now === 'break' ? t('cal.mkt.break') : s.state === 'closed' ? t('cal.mkt.closedToday') : t('cal.mkt.closed');
            const sub = s.now === 'open' ? t('cal.mkt.closesAt', { time: timeIn(s.at) }) : s.now === 'pre' || s.now === 'break' ? t('cal.mkt.opensAt', { time: timeIn(s.at) }) : s.reason || '';
            const cells = days
                .map((k) => {
                    const d = marketDay(m, k);
                    const cls = `day${k === today ? ' today' : ''}${k < today ? ' past' : ''}`;
                    if (d.state === 'closed') return `<td class="${cls}"><span class="cc-cell closed" style="--c:var(--t-closed)">${esc(t('cal.mkt.closed'))}<span> · ${esc(d.reason || '')}</span></span></td>`;
                    if (d.state === 'early') return `<td class="${cls}"><span class="cc-cell early" style="--c:var(--t-early)">${esc(t('cal.mkt.earlyAt', { time: timeIn(Date.parse(d.at)) }))}</span></td>`;
                    return `<td class="${cls}"><span class="cc-cell" style="--c:var(--t-open)">${esc(t('cal.mkt.open'))}</span></td>`;
                })
                .join('');
            return `<tr><td><div class="cc-ex"><span class="fl" aria-hidden="true">${flag(MKT_COUNTRY[m.id])}</span><div><b>${esc(MKT_NAME[m.id])}</b><small>${esc(m.city)} · ${m.open.map(([a, b]) => `${a}–${b}`).join(', ')} ${esc(t('cal.local'))}</small></div></div></td>
                <td><span class="cc-now" style="--c:${C}"><i></i><span>${esc(label)}<small>${esc(sub)}</small></span></span></td>${cells}</tr>`;
        })
        .join('');
    $('ca-mkt').innerHTML = head + `<tbody>${rows}</tbody>`;
}

// ---------------------------------------------------------------- agenda
const st = { country: 'all', groups: new Set(GROUPS.map((g) => g.id)), mine: false, impact: 'all', week: mondayOf(localDay()), weeks: 1, earlierOpen: false };
let events = [];
let loadedRange = '';
const follows = () => getFollows().countries;

function filtered() {
    const types = new Set(GROUPS.filter((g) => st.groups.has(g.id)).flatMap((g) => g.types));
    const mine = new Set(follows());
    if ([...mine].some((c) => EURO_AREA.has(c))) mine.add('EU');
    return events.filter(
        (e) =>
            types.has(e.type) &&
            (st.country === 'all' || e.country === st.country || (e.country === 'EU' && EURO_AREA.has(st.country))) &&
            (!st.mine || mine.has(e.country)) &&
            (st.impact === 'all' || e.impact === 'high' || (st.impact === 'medium' && e.impact === 'medium')),
    );
}

const count = (one, many, n) => (n === 1 ? t(one) : t(many, { n }));
const isPast = (e) => (e.at ? Date.parse(e.at) <= Date.now() : e.date < localDay());

function typeBadge(e) {
    return `<span class="cal-type cal-type--${e.type}">${ICONS[e.type]}<span>${esc(t(`cal.badge.${e.type}`))}</span></span>`;
}

/** Impact meter: 1-3 bars + label (count of bars keeps it readable without colour). */
function impactBadge(level) {
    return `<span class="imp" data-l="${esc(level)}" title="${esc(t(`cal.impTip.${level}`))}"><span class="imp-bars" aria-hidden="true"><i></i><i></i><i></i></span>${esc(t(`cal.imp.${level}`))}</span>`;
}

function rowHtml(e) {
    const origin = originTime(e);
    const timeCell = e.type === 'early_close'
        ? `<b>${timeIn(Date.parse(e.at))}</b><small>${esc(t('cal.closesEarly'))}</small>`
        : e.at
          ? `<b>${timeIn(Date.parse(e.at))}</b><small>${esc(t('cal.yourTimeShort'))}${origin ? `<br>${esc(origin)}` : ''}</small>`
          : `<span class="allday">${esc(t('cal.allDay'))}</span>`;
    let meta = '';
    if (e.type === 'central_bank' && e.approx) meta = `<span class="approx">${esc(t('cal.approxNote'))}</span>`;
    if (e.type === 'market_closed') meta = `${esc(e.reason || '')} · ${esc(t('cal.noTrading'))}`;
    if (e.type === 'data_release' && e.period) meta = esc(t('cal.period', { period: e.period }));
    const src = safeHref(e.source);
    const title = src ? `<a href="${src}" target="_blank" rel="noopener noreferrer">${esc(titleOf(e))}</a>` : esc(titleOf(e));
    const top = `${impactBadge(e.impact)}${typeBadge(e)}${e.type === 'market_closed' ? `<span class="ca-closed-tag">${esc(t('cal.mkt.closed'))}</span>` : ''}<span class="ca-ctry">${flag(e.country)} ${esc(nameOf(e.country))}</span>`;
    const released = isPast(e) && ['data_release', 'central_bank'].includes(e.type) && src
        ? `<div class="cal-released"><span class="cal-released-k">${esc(t('cal.released'))}</span><a href="${src}" target="_blank" rel="noopener noreferrer">${esc(t('cal.seeFigure'))} ${ICONS.ext}</a></div>`
        : '';
    const actions = `<div class="cal-actions">
        ${src ? `<a class="cal-act" href="${src}" target="_blank" rel="noopener noreferrer">${ICONS.ext}<span>${esc(t('cal.sourceShort'))}</span></a>` : ''}
        ${isPast(e) ? '' : `<a class="cal-act" href="${icsUrl(e.id)}" download>${ICONS.cal}<span>${esc(t('cal.addToCal'))}</span></a>`}
        <a class="cal-act" href="${newsHref(e.country)}">${ICONS.news}<span>${esc(t('cal.news'))}</span></a>
    </div>`;
    return `<li class="ca-row" data-t="${e.type}" data-i="${esc(e.impact)}"><div class="ca-time">${timeCell}</div>
        <div><div class="ca-top">${top}</div><p class="ca-title">${title}</p>${meta ? `<p class="ca-meta">${meta}</p>` : ''}${released}</div>${actions}</li>`;
}

function holsHtml(list) {
    if (!list.length) return '';
    const mine = new Set(follows());
    list = [...list].sort((a, b) => mine.has(b.country) - mine.has(a.country) || !!b.marketsOpen - !!a.marketsOpen || nameOf(a.country).localeCompare(nameOf(b.country)));
    const SHOW = 6;
    const li = (e, hide) => `<li class="${mine.has(e.country) ? 'mine' : ''}"${hide ? ' hidden' : ''}><span aria-hidden="true">${flag(e.country)}</span><span><b>${esc(nameOf(e.country))}</b> · ${esc(titleOf(e))}${e.estimated ? ` <span class="est">(${esc(t('cal.estimated'))})</span>` : ''}</span>${e.marketsOpen ? `<span class="cal-open-note">${esc(t('cal.openNote', { exchange: e.marketsOpen }))}</span>` : ''}</li>`;
    return `<div class="ca-hols"><div class="ca-hols-h">${ICONS.public_holiday}${esc(t('cal.type.public_holiday'))} · ${list.length}</div>
        <ul>${list.map((e, i) => li(e, i >= SHOW)).join('')}${list.length > SHOW ? `<li><button type="button" class="more" data-more>${esc(t('cal.plusMore', { n: list.length - SHOW }))}</button></li>` : ''}</ul></div>`;
}

function dayHtml(k, list) {
    const today = localDay();
    const rel = k === today ? t('cal.todayLabel') : k === addDays(today, 1) ? t('cal.tomorrow') : k === addDays(today, -1) ? t('cal.yesterday') : '';
    const main = list.filter((e) => e.type !== 'public_holiday');
    const hols = list.filter((e) => e.type === 'public_holiday');
    const nHigh = main.filter((e) => e.impact === 'high').length;
    const metaBits = [nHigh && t('cal.nHigh', { n: nHigh }), main.length && count('cal.oneEvent', 'cal.nEvents', main.length), hols.length && count('cal.oneHoliday', 'cal.nHolidays', hols.length)].filter(Boolean).join(' · ');
    let rows = '';
    let nowShown = k !== today;
    const nowLine = `<li class="ca-now"><i></i>${esc(t('cal.nowLine', { time: timeIn(Date.now()) }))}</li>`;
    for (const e of main) {
        if (!nowShown && e.at && Date.parse(e.at) > Date.now()) {
            rows += nowLine;
            nowShown = true;
        }
        rows += rowHtml(e);
    }
    if (!nowShown && main.length) rows += nowLine;
    return `<section class="ca-day${k === today ? ' is-today' : ''}${k < today ? ' is-past' : ''}" aria-labelledby="d-${k}">
        <header class="ca-day-h"><div class="ca-date" aria-hidden="true"><b>${dayNum(k)}</b><span>${esc(wdShort(k))}</span></div>
        <h3 id="d-${k}">${esc(dayLong(k))}${rel ? `<em>${esc(rel)}</em>` : ''}</h3><span class="meta">${esc(metaBits)}</span></header>
        ${main.length ? `<ol class="ca-rows">${rows}</ol>` : ''}
        ${holsHtml(hols)}
        ${!main.length && !hols.length ? `<p class="ca-meta" style="margin-left:66px">${esc(isWeekend(k) ? t('cal.weekendClosed') : t('cal.nothingDay'))}</p>` : ''}
    </section>`;
}

async function ensureLoaded(from, to) {
    const key = `${from}|${to}`;
    if (key === loadedRange) return;
    const root = $('list');
    root.setAttribute('aria-busy', 'true');
    try {
        events = await fetchEvents({ from: addDays(from, -1), to: addDays(to, 1) });
        loadedRange = key;
    } catch {
        events = [];
        root.innerHTML = `<div class="cal-empty"><p>${esc(t('cal.error'))}</p></div>`;
        throw new Error('calendar unavailable');
    } finally {
        root.removeAttribute('aria-busy');
    }
}

async function render() {
    const from = st.week;
    const to = addDays(st.week, 7 * st.weeks - 1);
    try {
        await ensureLoaded(from, to);
    } catch {
        return;
    }
    const inRange = filtered().filter((e) => {
        const d = eventDay(e);
        return d >= from && d <= to;
    });
    const thisWeek = from === mondayOf(localDay());
    const span = `${dayNum(from)} ${monthShort(from) === monthShort(to) ? '' : `${monthShort(from)} `}– ${dayNum(to)} ${monthShort(to)}`;
    const nextWeek = from === addDays(mondayOf(localDay()), 7);
    $('week-label').textContent = thisWeek ? `${t('cal.thisWeek')} · ${span}` : nextWeek ? `${t('cal.nextWeekLabel')} · ${span}` : span;
    const nMain = inRange.filter((e) => e.type !== 'public_holiday').length;
    $('week-meta').textContent = `${count('cal.oneEvent', 'cal.nEvents', nMain)} · ${count('cal.oneHoliday', 'cal.nHolidays', inRange.length - nMain)} · ${tzAbbr()}`;
    for (const g of GROUPS) {
        const n = events.filter((e) => g.types.includes(e.type) && eventDay(e) >= from && eventDay(e) <= to).length;
        $('f-types').querySelector(`[data-g="${g.id}"] [data-n]`).textContent = n;
    }
    const byDay = new Map();
    for (const e of inRange) {
        const d = eventDay(e);
        if (!byDay.has(d)) byDay.set(d, []);
        byDay.get(d).push(e);
    }
    const days = [...byDay.keys()].sort();
    const today = localDay();
    const earlier = days.filter((k) => k < today);
    let html = '';
    if (earlier.length && !st.earlierOpen) {
        const ev = earlier.flatMap((k) => byDay.get(k));
        const n = ev.filter((e) => e.type !== 'public_holiday').length;
        html += `<button type="button" class="ca-earlier" id="earlier"><b>${esc(t('cal.earlier'))}</b> <span class="rng">${esc(dayShort(earlier[0]))} – ${esc(dayShort(earlier.at(-1)))} · </span>${esc(count('cal.oneEvent', 'cal.nEvents', n))} · ${esc(count('cal.oneHoliday', 'cal.nHolidays', ev.length - n))}<span class="arrow">${esc(t('cal.show'))} ▾</span></button>`;
    }
    for (const k of days) if (st.earlierOpen || k >= today) html += dayHtml(k, byDay.get(k));
    if (!days.length) html = `<div class="cal-empty"><p><b>${esc(t('cal.none'))}</b></p><p>${esc(st.mine && !follows().length ? t('cal.noFollows') : t('cal.tryAll'))}</p><button type="button" class="cal-btn" id="reset">${esc(t('cal.reset'))}</button></div>`;
    const root = $('list');
    root.innerHTML = html;
    root.querySelector('#earlier')?.addEventListener('click', () => {
        st.earlierOpen = true;
        render();
    });
    root.querySelector('#reset')?.addEventListener('click', resetFilters);
    root.querySelectorAll('[data-more]').forEach((b) =>
        b.addEventListener('click', () => {
            b.closest('ul').querySelectorAll('li[hidden]').forEach((li) => (li.hidden = false));
            b.parentElement.remove();
        }),
    );
}

// ---------------------------------------------------------------- filters
async function initFilters() {
    let countries = [];
    try {
        const r = await fetch('/api/countries?limit=250');
        countries = (await r.json()).data.map((c) => c.iso2.toUpperCase());
    } catch {
        countries = [];
    }
    const mine = follows();
    const opt = (iso) => `<option value="${iso}">${flag(iso)} ${esc(nameOf(iso))}</option>`;
    const all = ['EU', ...countries].sort((a, b) => nameOf(a).localeCompare(nameOf(b), loc));
    $('f-country').innerHTML = `<option value="all">${esc(t('cal.allCountries'))}</option>${mine.length ? `<optgroup label="${esc(t('cal.yourCountries'))}">${mine.map(opt).join('')}</optgroup>` : ''}<optgroup label="${esc(t('cal.allCountries'))}">${all.map(opt).join('')}</optgroup>`;
    const qc = (new URLSearchParams(location.search).get('country') || '').toUpperCase();
    if (qc && all.includes(qc)) {
        $('f-country').value = qc;
        st.country = qc;
    }
    $('f-country').addEventListener('change', () => {
        st.country = $('f-country').value;
        const u = new URL(location.href);
        if (st.country === 'all') u.searchParams.delete('country');
        else u.searchParams.set('country', st.country);
        history.replaceState(null, '', u);
        render();
    });
    $('f-types').setAttribute('aria-label', t('cal.types'));
    $('f-types').innerHTML = GROUPS.map((g) => `<button type="button" class="cal-chip" data-g="${g.id}" aria-pressed="true"><span class="sw"></span><span class="lbl">${esc(t(`cal.group.${g.id}`))}</span><span class="cal-chip-n" data-n></span></button>`).join('');
    $('f-types').addEventListener('click', (ev) => {
        const b = ev.target.closest('.cal-chip');
        if (!b) return;
        if (st.groups.has(b.dataset.g)) st.groups.delete(b.dataset.g);
        else st.groups.add(b.dataset.g);
        b.setAttribute('aria-pressed', String(st.groups.has(b.dataset.g)));
        render();
    });
    $('f-mine').addEventListener('click', (ev) => {
        st.mine = !st.mine;
        ev.currentTarget.setAttribute('aria-pressed', String(st.mine));
        render();
    });
    $('f-imp').setAttribute('aria-label', t('cal.impact'));
    $('f-imp').innerHTML = `<span>${esc(t('cal.impact'))}</span>` + [['all', 'cal.impAll'], ['medium', 'cal.impMediumUp'], ['high', 'cal.impHighOnly']]
        .map(([v, k]) => `<button type="button" role="radio" data-v="${v}" aria-checked="${v === st.impact}">${esc(t(k))}</button>`).join('');
    $('f-imp').addEventListener('click', (ev) => {
        const b = ev.target.closest('button');
        if (!b) return;
        st.impact = b.dataset.v;
        $('f-imp').querySelectorAll('button').forEach((x) => x.setAttribute('aria-checked', String(x === b)));
        render();
    });
    $('ca-legend').innerHTML = ['high', 'medium', 'low'].map((l) => `<span>${impactBadge(l)}${esc(t(`cal.impTip.${l}`))}</span>`).join('') + `<span>${esc(t('cal.impNote'))}</span>`;
    $('prev').addEventListener('click', () => {
        st.week = addDays(st.week, -7);
        st.weeks = 1;
        render();
    });
    $('next-w').addEventListener('click', () => {
        st.week = addDays(st.week, 7);
        st.weeks = 1;
        render();
    });
    $('more').addEventListener('click', () => {
        st.weeks = Math.min(st.weeks + 1, 8);
        render();
    });
    $('subscribe').addEventListener('click', openSubscribe);
}

function resetFilters() {
    st.country = 'all';
    st.mine = false;
    st.impact = 'all';
    st.groups = new Set(GROUPS.map((g) => g.id));
    $('f-country').value = 'all';
    $('f-mine').setAttribute('aria-pressed', 'false');
    $('f-imp').querySelectorAll('button').forEach((x) => x.setAttribute('aria-checked', String(x.dataset.v === 'all')));
    $('f-types').querySelectorAll('.cal-chip').forEach((b) => b.setAttribute('aria-pressed', 'true'));
    render();
}

// ---------------------------------------------------------------- subscribe dialog
function feedUrl() {
    const p = new URLSearchParams();
    const cs = st.mine ? follows() : st.country !== 'all' ? [st.country] : [];
    if (cs.length) p.set('countries', cs.join(','));
    const types = GROUPS.filter((g) => st.groups.has(g.id)).flatMap((g) => g.types);
    if (types.length < 5) p.set('types', types.join(','));
    if (st.impact !== 'all') p.set('impact', st.impact === 'high' ? 'high' : 'high,medium');
    const q = p.toString().replace(/%2C/g, ',');
    return `${location.host}/api/calendar.ics${q ? `?${q}` : ''}`;
}
function openSubscribe() {
    const path = feedUrl();
    const https = `${location.protocol}//${path}`;
    const webcal = `webcal://${path}`;
    const d = document.createElement('div');
    d.className = 'cal-dialog';
    d.setAttribute('role', 'dialog');
    d.setAttribute('aria-modal', 'true');
    d.setAttribute('aria-labelledby', 'sub-h');
    d.innerHTML = `<div class="cal-dialog-box">
        <button type="button" class="cal-dialog-x" aria-label="${esc(t('close'))}">×</button>
        <h2 id="sub-h">${esc(t('cal.subscribeTitle'))}</h2>
        <p class="cal-dialog-sub">${esc(t('cal.subscribeHint'))}</p>
        <label class="cal-field"><span>${esc(t('cal.feedAddress'))}</span><input readonly value="${esc(https)}"></label>
        <div class="cal-dialog-actions">
            <button type="button" class="cal-btn cal-btn-primary" data-copy>${esc(t('cal.copyLink'))}</button>
            <a class="cal-btn" href="${esc(webcal)}">Apple / Outlook</a>
            <a class="cal-btn" href="https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcal)}" target="_blank" rel="noopener noreferrer">Google Calendar</a>
        </div>
        <p class="cal-fine">${esc(t('cal.subFine'))}</p>
    </div>`;
    const prev = document.activeElement;
    const close = () => {
        d.remove();
        document.removeEventListener('keydown', onKey);
        prev?.focus?.();
    };
    const onKey = (ev) => ev.key === 'Escape' && close();
    d.addEventListener('click', (ev) => ev.target === d && close());
    d.querySelector('.cal-dialog-x').addEventListener('click', close);
    d.querySelector('[data-copy]').addEventListener('click', (ev) => {
        navigator.clipboard?.writeText(https).catch(() => {});
        ev.currentTarget.textContent = t('cal.copied');
    });
    document.addEventListener('keydown', onKey);
    document.body.append(d);
    d.querySelector('[data-copy]').focus();
}

// ---------------------------------------------------------------- boot
(async () => {
    await initFilters();
    render();
    const mon = mondayOf(localDay());
    try {
        const [next, cl] = await Promise.all([
            fetch('/api/calendar/next').then((r) => r.json()),
            fetchEvents({ from: addDays(mon, -1), to: addDays(mon, 6), types: ['market_closed', 'early_close'] }),
        ]);
        decisions = next.data || [];
        closures = cl;
    } catch {
        decisions = [];
    }
    renderBanks();
    renderMarkets();
    setInterval(renderBanks, 30000);
    setInterval(renderMarkets, 60000);
})();
